import {
  EViewerMode,
  ProjectDetailContext,
} from "../Interfaces/ProjectDetailInterfaces";
import { Q3DButton } from "../../Parts/Q3DButton";
import { Q3DTabPanel } from "../../Parts/Q3DTabPanel";
import ToolIcon from "../../../components/Icon";
import DataDesign from "../../../../images/icons/cubes.svg";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { useTheme } from "@mui/material";

// 配置レイアウト変更を他コンポーネント (ドロワー) に通知するイベント名。
export const IFC_LAYOUT_CHANGED_EVENT = "ifc-layout-changed";

// 3D 上で配置済み IFC オブジェクトを選択 / 解除したことをドロワー (一覧) に
// 通知するイベント名。detail.id に選択された配置済みオブジェクトの id (or null)。
export const IFC_OBJECT_SELECTED_EVENT = "ifc-object-selected";

// 3D 上で配置済み IFC オブジェクトを編集 (移動 / 回転 / スケール) したことを
// ドロワーに通知するイベント名。detail.id に編集された配置済みオブジェクトの id。
// (自動保存はせず、Save ボタンで scene 全体をスナップショット保存する。)
export const IFC_OBJECT_EDITED_EVENT = "ifc-object-edited";

// 3D 上でオブジェクトを Viewer (canvas) の外へドラッグ&ドロップしたことを
// ドロワーに通知するイベント名。
// detail.id が配置済みオブジェクトの id なら削除予約 (Save で確定)、
// null ならプレースホルダー (新規・未保存) なので選択解除して非表示にする。
export const IFC_OBJECT_DROP_OUTSIDE_EVENT = "ifc-object-drop-outside";

// 配置済みオブジェクトの削除を「予約」したことを通知するイベント名。detail.id に
// 対象の配置済みオブジェクト id。自動削除はせず Save スナップショットで反映する。
export const IFC_OBJECT_DELETE_REQUESTED_EVENT = "ifc-object-delete-requested";

// Save ボタン → ドロワーへ「シーン全体をスナップショット保存せよ」と依頼する。
export const IFC_REQUEST_SAVE_EVENT = "ifc-request-save";
// ドロワー → Save ボタンへ保存完了を通知する。detail.ok に成否 (true で dirty 解除)。
export const IFC_SAVE_DONE_EVENT = "ifc-save-done";
// ドラッグ配置がシーンに入ったら発火 → 下ボタンと同じ仕組みでドロワーを閉じる。
export const IFC_PLACEMENT_DRAG_ENTER_EVENT = "ifc-placement-drag-enter";

type Props = {
  width?: string;
};

// IFC 配置モード (IFC ボタン押下) のときに表示されるタブ。
// 「Save Design Data」ボタンを持つ。押すとシーン全体 (配置済み IFC 一覧) を
// スナップショットとして 1 リクエストで保存し (追加 / 編集 / 削除を一括)、サーバ側
// が bake をトリガする。自動保存は一切なく、明示的にこのボタンでのみ保存される。
export const IfcTabPanel = (props: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { selectedIfcObjectId } = useContext(ProjectDetailContext);

  const [saving, setSaving] = useState<boolean>(false);
  // 未保存の変更 (編集 / 削除予約) があるか。新規配置は selectedIfcObjectId で判定。
  const [dirty, setDirty] = useState<boolean>(false);
  useEffect(() => {
    const markDirty = () => setDirty(true);
    const onDone = (e: Event) => {
      setSaving(false);
      if ((e as CustomEvent).detail?.ok) setDirty(false);
    };
    document.addEventListener(IFC_OBJECT_EDITED_EVENT, markDirty);
    document.addEventListener(IFC_OBJECT_DELETE_REQUESTED_EVENT, markDirty);
    document.addEventListener(IFC_SAVE_DONE_EVENT, onDone);
    return () => {
      document.removeEventListener(IFC_OBJECT_EDITED_EVENT, markDirty);
      document.removeEventListener(IFC_OBJECT_DELETE_REQUESTED_EVENT, markDirty);
      document.removeEventListener(IFC_SAVE_DONE_EVENT, onDone);
    };
  }, []);

  // IFC オブジェクトが選択されている (= 新規配置中) か。
  const isIfcObjectSelected = selectedIfcObjectId != null;
  const canSave = isIfcObjectSelected || dirty;

  // 実際の PUT はドロワーが placedObjects + viewer からスナップショットを組み立てて
  // 実行する (メタデータを持っているため)。ここでは依頼を発火するだけ。
  const handleSave = () => {
    if (saving || !canSave) return;
    setSaving(true);
    document.dispatchEvent(new CustomEvent(IFC_REQUEST_SAVE_EVENT));
  };

  // 無効時の背景・枠線はテーマ既定の disabled スタイルを使う。
  const disabledStyle = (theme.palette.btn as any).default.disabled;

  return (
    <Q3DTabPanel
      value={EViewerMode.IFC_PLACEMENT}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: "var(--space-space--4, 4px)",
        width: props.width,
        paddingLeft: "4px",
      }}
    >
      <Q3DButton
        text={t("SAVE_DESIGN_DATA")}
        icon={
          <ToolIcon
            imagePath={DataDesign}
            color="#FFFFFF"
            width={22}
            height={22}
          />
        }
        height={"36px"}
        onClick={handleSave}
        disabled={!canSave || saving}
        sx={{
          // テキストは常に白
          color: "#FFFFFF",
          // 有効時は配置色 (青)
          background: theme.palette.base.ymdBackground,
          "&:hover": {
            background: theme.palette.base.ymdBackground,
          },
          // 無効時は背景・枠線をテーマ既定の disabled に、文字は白のまま
          "&.Mui-disabled": {
            background: disabledStyle.backgroundColor,
            border: disabledStyle.border,
            color: "#FFFFFF",
          },
        }}
      />
    </Q3DTabPanel>
  );
};
